import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getActiveAds } from "../../../api/api";
import { CircularProgress, Alert, Box, useMediaQuery, useTheme, IconButton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "./Carousel.css";
// Demo images for ads
import DemoLandscape1 from "../../../assests/landscape/1.png";
import DemoLandscape2 from "../../../assests/landscape/2.png";
import DemoLandscape3 from "../../../assests/landscape/3.png";
import DemoLandscape4 from "../../../assests/landscape/4.png";
import DemoPortrait1 from "../../../assests/Potrait/1.png";
import DemoPortrait2 from "../../../assests/Potrait/2.png";
import DemoPortrait3 from "../../../assests/Potrait/3.jpg";
import DemoPortrait4 from "../../../assests/Potrait/4.jpg";

// Custom arrow components
const PrevArrow = ({ onClick }) => (
    <div className="carousel-arrow carousel-arrow-prev" onClick={onClick}>
        <ArrowBackIosIcon />
    </div>
);

const NextArrow = ({ onClick }) => (
    <div className="carousel-arrow carousel-arrow-next" onClick={onClick}>
        <ArrowForwardIosIcon />
    </div>
);

const Carousel = ({ imageType = null, branchId: propBranchId = null, clientId: propClientId = null, token: propToken = null, interval: propInterval = null }) => {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
    const sliderRef = React.useRef(null);
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Get parameters from props first, then fall back to URL params
    const branchId = propBranchId || searchParams.get("branch_id");
    const token = propToken || searchParams.get("token");
    const clientId = propClientId || searchParams.get("client_id");
    const interval = propInterval || parseInt(searchParams.get("interval")) || 5000; // Default 5 seconds

    // Demo ads for demo mode
    const demoLandscapeAds = [
        {
            id: "demo-landscape-1",
            title: "Demo Landscape Ad 1",
            image_url: DemoLandscape1,
            description: "Sample landscape advertisement",
        },
        {
            id: "demo-landscape-2",
            title: "Demo Landscape Ad 2",
            image_url: DemoLandscape2,
            description: "Sample landscape advertisement",
        },
        {
            id: "demo-landscape-3",
            title: "Demo Landscape Ad 3",
            image_url: DemoLandscape3,
            description: "Sample landscape advertisement",
        },
        {
            id: "demo-landscape-4",
            title: "Demo Landscape Ad 4",
            image_url: DemoLandscape4,
            description: "Sample landscape advertisement",
        },
    ];

    const demoPortraitAds = [
        {
            id: "demo-portrait-1",
            title: "Demo Portrait Ad 1",
            image_url: DemoPortrait1,
            description: "Sample portrait advertisement",
        },
        {
            id: "demo-portrait-2",
            title: "Demo Portrait Ad 2",
            image_url: DemoPortrait2,
            description: "Sample portrait advertisement",
        },
        {
            id: "demo-portrait-3",
            title: "Demo Portrait Ad 3",
            image_url: DemoPortrait3,
            description: "Sample portrait advertisement",
        },
        {
            id: "demo-portrait-4",
            title: "Demo Portrait Ad 4",
            image_url: DemoPortrait4,
            description: "Sample portrait advertisement",
        },
    ];

    // Check if we're in demo mode (internal access without external parameters)
    const isDemoMode = !branchId && !token && !clientId;

    // Check if we're in an iframe and set up dimension communication
    // Only do this if we're actually in an iframe (not when used as a direct component)
    const isInIframe = window !== window.top && propBranchId === null; // Only iframe mode when no propBranchId (legacy iframe usage)

    useEffect(() => {
        fetchActiveAds();
    }, [imageType, branchId, token, clientId, isDemoMode]);

    // Preload images when ads are loaded
    useEffect(() => {
        if (ads.length > 0) {
            // Preload all carousel images
            ads.forEach((ad) => {
                const img = new Image();
                img.src = ad.image_url;
            });
        }
    }, [ads]);

    // Nudge layout on mount and when ads change to avoid initial oversizing until resize
    useEffect(() => {
        const nudge = () => {
            try {
                // Force browser and slick to recalc sizes
                window.dispatchEvent(new Event('resize'));
                if (sliderRef.current && typeof sliderRef.current.slickGoTo === 'function') {
                    sliderRef.current.slickGoTo(0, true);
                }
            } catch (_) {}
        };

        const timeoutId = setTimeout(nudge, 150);
        return () => clearTimeout(timeoutId);
    }, [ads.length]);

    // Auto-refresh ads every 60 seconds
    useEffect(() => {
        const refreshInterval = setInterval(() => {
            console.log("Auto-refreshing ads...");
            fetchActiveAds();
        }, 60000); // 60 seconds

        return () => clearInterval(refreshInterval);
    }, [imageType, branchId, token, clientId, isDemoMode]);

    const fetchActiveAds = async () => {
        try {
            setLoading(true);
            console.log("Carousel fetchActiveAds - Starting fetch:", {
                imageType,
                branchId,
                token,
                clientId,
                isDemoMode,
            });

            // Check demo mode first for internal access
            if (isDemoMode && !branchId && !token && !clientId) {
                console.log("Demo mode detected, using demo ads immediately");
                if (imageType) {
                    const demoAds =
                        imageType === "landscape"
                            ? demoLandscapeAds
                            : demoPortraitAds;
                    setAds(demoAds);
                } else {
                    setAds([...demoLandscapeAds, ...demoPortraitAds]);
                }
                setError(null);
                return;
            }

            if (imageType) {
                // Fetch specific image type (landscape or portrait)
                const response = await getActiveAds(
                    imageType,
                    clientId,
                    branchId
                );
                console.log(`API response for ${imageType}:`, response);

                if (response.success && response.data.length > 0) {
                    console.log(`Using real ${imageType} ads:`, response.data);
                    setAds(response.data);
                } else {
                    console.log(`No real ${imageType} ads available`);
                    setAds([]);
                }
            } else {
                // Fetch both landscape and portrait ads
                const [landscapeResponse, portraitResponse] = await Promise.all([
                    getActiveAds("landscape", clientId, branchId),
                    getActiveAds("portrait", clientId, branchId),
                ]);

                console.log("Landscape API response:", landscapeResponse);
                console.log("Portrait API response:", portraitResponse);

                let allAds = [];

                // Use real ads if available
                if (landscapeResponse.success) {
                    allAds.push(...landscapeResponse.data);
                }
                if (portraitResponse.success) {
                    allAds.push(...portraitResponse.data);
                }

                // Sort by sort_order to maintain proper sequence
                allAds.sort(
                    (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
                );
                setAds(allAds);

                if (allAds.length === 0) {
                    console.log("No real ads available for carousel");
                    setError("No active ads available");
                } else {
                    console.log("Using real ads for carousel:", allAds);
                }
            }
            setError(null);
        } catch (err) {
            console.error("Error fetching ads:", err);
            // In demo mode, use demo ads even if there's an error
            if (isDemoMode) {
                console.log("Demo mode: Using demo ads due to error");
                if (imageType) {
                    const demoAds =
                        imageType === "landscape"
                            ? demoLandscapeAds
                            : demoPortraitAds;
                    setAds(demoAds);
                } else {
                    setAds([...demoLandscapeAds, ...demoPortraitAds]);
                }
            } else {
                setError("Error loading ads");
            }
        } finally {
            setLoading(false);
        }
    };

    // Check if used as direct component (not iframe)
    const isDirectComponent = propBranchId !== null;

    if (loading) {
        // When used as direct component, show minimal loading state
        if (isDirectComponent) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "400px",
                        width: "100%",
                        backgroundColor: "transparent",
                    }}
                >
                    <CircularProgress size={40} />
                </Box>
            );
        }
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                    backgroundColor: "#000",
                    "@media (max-width: 768px)": {
                        height: "100vh",
                    },
                }}
            >
                <CircularProgress sx={{ color: "#fff" }} />
            </Box>
        );
    }

    if (error) {
        // When used as direct component, return null on error
        if (isDirectComponent) {
            return null;
        }
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                    backgroundColor: "#000",
                    padding: 2,
                    "@media (max-width: 768px)": {
                        height: "100vh",
                        padding: 1,
                    },
                }}
            >
                <Alert severity="error" sx={{ backgroundColor: "#fff" }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (ads.length === 0) {
        // When used as direct component, return null when no ads
        if (isDirectComponent) {
            return null;
        }
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    width: "100%",
                    backgroundColor: "#000",
                    padding: 2,
                    "@media (max-width: 768px)": {
                        height: "100vh",
                        padding: 1,
                    },
                }}
            >
                <Alert severity="info" sx={{ backgroundColor: "#fff" }}>
                    {imageType
                        ? `No active ${imageType} ads available`
                        : "No active ads available"}
                </Alert>
            </Box>
        );
    }

    // Slick carousel settings
    const settings = {
        dots: true,
        infinite: ads.length > 1,
        speed: 600,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: ads.length > 1,
        autoplaySpeed: interval,
        pauseOnHover: !isMobile,
        pauseOnFocus: false,
        pauseOnDotsHover: false,
        arrows: ads.length > 1,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        cssEase: "ease-in-out",
        fade: false,
        swipe: true,
        swipeToSlide: true,
        touchMove: true,
        touchThreshold: 5,
        adaptiveHeight: false,
        centerMode: false,
        focusOnSelect: false,
        initialSlide: 0,
        lazyLoad: "ondemand",
    };

    // Different styling based on image type and context
    // When used as direct component (not iframe), use auto height and transparent background
    const containerStyle = {
        width: "100%",
        height: isInIframe ? "100%" : (isDirectComponent ? "auto" : "100dvh"),
        margin: "0",
        padding: "0",
        boxSizing: "border-box",
        backgroundColor: (isInIframe || isDirectComponent) ? "transparent" : "#000",
        minHeight: isDirectComponent ? "400px" : "auto",
    };

    // Function to get image style based on context
    const getImageStyle = (ad) => {
        // Use container-bound sizing for both iframe and fullscreen to avoid cropping
        // Make images fully responsive - CSS handles media queries
        return {
            objectFit: "contain",
            objectPosition: "center center",
            borderRadius: "0",
            display: "block",
            margin: "auto",
            boxShadow: "none",
            border: "none",
            outline: "none",
            width: "auto",
            height: "auto",
            maxWidth: "100%",
            maxHeight: "100%",
            minWidth: 0,
            minHeight: 0,
        };
    };

    return (
        <div
            className={`carousel-fullscreen ${isInIframe ? "iframe-mode" : ""} ${isDirectComponent ? "direct-component-mode" : ""}`}
            style={containerStyle}
        >
            <Slider {...settings} ref={sliderRef}>
                {ads.map((ad) => (
                    <div key={ad.id}>
                        <div className="carousel-slide-container">
                            <img
                                src={ad.image_url}
                                alt={ad.title}
                                style={getImageStyle(ad)}
                                onLoad={() => {
                                    try {
                                        // Recalculate after each image loads
                                        window.dispatchEvent(new Event('resize'));
                                        if (sliderRef.current && typeof sliderRef.current.slickGoTo === 'function') {
                                            sliderRef.current.slickGoTo(0, true);
                                        }
                                    } catch (_) {}
                                }}
                                loading="eager"
                                fetchPriority="high"
                                onError={(e) => {
                                    console.error('Failed to load carousel image:', ad.image_url);
                                    // Hide broken images
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                    </div>
                ))}
            </Slider>
        </div>
    );
};

export default Carousel;
